import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { API_ENDPOINTS } from '../config/api';

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) return;
    setSending(true);

    try {
      const response = await fetch(API_ENDPOINTS.SEND_CONTACT_FORM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: form.email,
          userName: form.name,
          userPhone: form.phone,
          subject: form.subject,
          message: form.message
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Message sent! We\'ll get back to you soon.');
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
        setTermsAccepted(false);
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Helmet>
        <title>Contact Us – MyMoveAdvisor</title>
        <meta name="description" content="Have a question about MyMoveAdvisor? Get in touch with our team. We're here to help with any questions about moving quotes, movers, or our services." />
        <link rel="canonical" href="https://mymoveadvisor.com/contact" />
        <meta property="og:title" content="Contact Us – MyMoveAdvisor" />
        <meta property="og:description" content="Get in touch with our team. We're here to help." />
        <meta property="og:url" content="https://mymoveadvisor.com/contact" />
      </Helmet>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Contact</h1>
      <p className="text-gray-600 mb-8">Have a question about MyMoveAdvisor? Send us a message and we'll get back to you.</p>

      <form onSubmit={submit} className="bg-white border rounded-xl p-6 space-y-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Name</label>
            <input value={form.name} onChange={setField('name')} className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none transition-all" placeholder="Your full name" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Email</label>
            <input type="email" value={form.email} onChange={setField('email')} className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none transition-all" placeholder="you@example.com" required />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Phone</label>
            <input value={form.phone} onChange={setField('phone')} className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none transition-all" placeholder="(201) 555-0123" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1">Subject</label>
            <input value={form.subject} onChange={setField('subject')} className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none transition-all" placeholder="How can we help?" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-1">Message</label>
          <textarea rows={6} value={form.message} onChange={setField('message')} className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:outline-none transition-all" placeholder="Write your message..." required />
        </div>

        {/* Terms and Policy Checkbox */}
        <div className="flex items-start gap-3 py-2">
          <input
            type="checkbox"
            id="terms"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
          />
          <label htmlFor="terms" className="text-sm text-gray-600 leading-tight">
            By providing your mobile number, you agree to receive transactional <strong>SMS</strong> messages from My Move Advisor including verification codes and account-related notifications. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance.
          </label>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={sending || !termsAccepted}
            className={`w-full sm:w-auto px-8 py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-all shadow-md ${sending || !termsAccepted ? 'bg-gray-300 cursor-not-allowed shadow-none' : 'bg-orange-600 hover:bg-orange-700 active:transform active:scale-95'
              }`}
          >
            {sending ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </button>
        </div>
      </form>

      {/* Modern Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900">Terms & Privacy Policy</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-8 overflow-y-auto leading-relaxed text-gray-700 custom-scrollbar">
              <div className="space-y-8">
                <section>
                  <h3 className="text-base font-bold text-orange-600 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                    Terms of Service
                  </h3>
                  <div className="space-y-4">
                    <p className="text-xs md:text-sm">By using <strong>MyMoveAdvisor</strong>, you agree to these Terms. Our platform provides tools for requesting moving quotes, comparing providers, and managing communications.</p>
                    <ul className="list-disc pl-5 space-y-2 text-xs md:text-sm">
                      <li>We are an advisory platform and not a direct moving carrier.</li>
                      <li>Users must provide accurate information for reliable quotes.</li>
                      <li>Any disputes arising from services are strictly between you and the selected mover.</li>
                      <li>We reserve the right to suspend accounts that violate our safety guidelines.</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h3 className="text-base font-bold text-orange-600 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-600 rounded-full"></span>
                    Privacy Policy
                  </h3>
                  <div className="space-y-4 text-xs md:text-sm">
                    <p>We respect your privacy and are committed to protecting it. Here is how we handle your data:</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li><strong>Collection:</strong> We collect your name, email, phone, and moving requirements to provide our service.</li>
                      <li><strong>Usage:</strong> Your data is used strictly to facilitate quote requests and communicate updates.</li>
                      <li><strong>Sharing:</strong> We only share your data with vetted service providers (movers) whom you choose to engage with.</li>
                      <li><strong>Protection:</strong> We implement administrative and technical safeguards to keep your personal information secure.</li>
                    </ul>
                  </div>
                </section>

                <div className="bg-orange-50 p-4 rounded-lg border border-orange-100 text-sm text-orange-800 italic">
                  Last updated: February 4, 2026. For more info, contact us at support@mymoveadvisor.com.
                </div>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setShowTermsModal(false);
                }}
                className="bg-orange-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-orange-700 transition-colors shadow-sm"
              >
                Accept and Close
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Contact;
